/**
 * Hook para carregar configuração do widget
 * Busca configuração pública da inbox via Edge Function
 * 
 * @version 1.1.0 - Adicionado suporte a preview na aplicação principal
 */

import { useState, useEffect } from 'react';
import type { WidgetConfig } from '../types';
import { getSupabaseUrl } from '../utils/supabaseConfig';
import { createLogger } from '@/lib/logger';

// Logger centralizado - não expõe stack/dados sensíveis no console em produção
const logger = createLogger('UaizyChatWidget');

export const useWidgetConfig = (inboxId: string) => {
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const supabaseUrl = getSupabaseUrl();
        if (!supabaseUrl) {
          throw new Error('URL do Supabase não configurada');
        }

        // Busca configuração da Edge Function com cache desabilitado
        // para garantir que alterações sejam refletidas imediatamente
        const response = await fetch(
          `${supabaseUrl}/functions/v1/webchat-config?inbox_id=${inboxId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            cache: 'no-store', // Evita cache do navegador
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Falha ao carregar configuração');
        }

        const data = await response.json();
        setConfig(data);
      } catch (err) {
        logger.debug('Erro ao carregar config do widget (silenciado)');
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
        
        // Config padrão em caso de erro
        setConfig({
          inbox_id: inboxId,
          primary_color: '#6366f1',
          greeting_message: 'Olá! Como posso ajudar?',
          position: 'bottom-right',
          show_branding: true,
          auto_open_delay: null,
          require_name: true,
          require_email: false,
          require_phone: false,
          offline_message: null,
          custom_css: null,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (inboxId) {
      fetchConfig();
    }
  }, [inboxId]);

  return { config, isLoading, error };
};
